import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, MailCheck, KeyRound } from "lucide-react";
import HealthCirclesLogo from "@/components/HealthCirclesLogo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-6 py-12">
      <div className="w-full max-w-md" data-testid="forgot-password-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-2xl bg-[#0A4C36] text-white flex items-center justify-center">
            <HealthCirclesLogo size={22} />
          </div>
          <div className="text-lg font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>HealthCircles</div>
        </div>

        {sent ? (
          <div className="text-center" data-testid="forgot-password-sent">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#E6F0EC] text-[#0A4C36] flex items-center justify-center mb-6">
              <MailCheck size={26} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>
              Check your inbox
            </h1>
            <p className="mt-3 text-sm text-[#47554D]">
              If an account exists for <b className="text-[#1F2A24]">{email}</b>, we&rsquo;ve emailed a password-reset link.
              The link is valid for 30 minutes.
            </p>
            <div className="mt-8">
              <Link to="/login" className="btn-outline-emerald">Back to sign in</Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-[#E6F0EC] text-[#0A4C36] flex items-center justify-center mb-6">
              <KeyRound size={26} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A4C36] text-center" style={{ fontFamily: "Instrument Sans" }}>
              Forgot your password?
            </h1>
            <p className="mt-3 text-sm text-center text-[#47554D]">
              Enter the email you used to sign up. We&rsquo;ll send you a secure link to choose a new password.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="field-label block mb-2">Email</label>
                <input
                  data-testid="forgot-email"
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-clean"
                  placeholder="doctor@clinic.com"
                />
              </div>
              <button type="submit" disabled={busy} className="btn-emerald w-full justify-center" data-testid="forgot-submit">
                {busy ? "Sending…" : (<>Send reset link <ArrowRight size={16} /></>)}
              </button>
            </form>

            <div className="mt-6 text-sm text-center text-[#6C7B72]">
              Remembered it?{" "}
              <Link to="/login" className="text-[#0A4C36] font-semibold hover:underline">Back to sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
